import { Grid, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import HelpIcon from '@material-ui/icons/Help';
import InfoIcon from '@material-ui/icons/Info';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import ReportProblem from '@material-ui/icons/ReportProblem';
import Checkmark from 'src/common/media/Checkmark.svg';
import clsx from 'clsx';
import React, { CSSProperties } from 'react';
import { InterpolationArguments, useTranslation } from 'react-i18next';
import { pushEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { VerticalAlignment } from 'src/common/enumerations/verticalAlignment';
import { IMessageModal, MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import InteractiveDialogueContainer, { IModal } from '../interactive-dialogue-container';
import { styles } from './interactive-dialog-content-styles';

export const DialogIcon: React.FC<EmeraldDialogFrameProps> = (
  props: EmeraldDialogFrameProps,
): JSX.Element => {
  const classes = styles();
  switch (props.type) {
    case EmeraldDialogTypes.Status:
    case EmeraldDialogTypes.Info:
      return <InfoIcon classes={{ root: `${classes.icon} ${classes.info}` }} />;
    case EmeraldDialogTypes.InfoDecision:
      return <HelpIcon classes={{ root: `${classes.icon} ${classes.decision}` }} />;
    case EmeraldDialogTypes.Prohibit:
      return <RemoveCircleIcon classes={{ root: `${classes.icon} ${classes.prohibit}` }} />;
    case EmeraldDialogTypes.Warning:
    case EmeraldDialogTypes.None:
      return <ReportProblemOutlinedIcon classes={{ root: `${classes.icon} ${classes.warning}` }} />;
    case EmeraldDialogTypes.Error:
      return <ReportProblem classes={{ root: `${classes.icon} ${classes.warning}` }} />;
    case EmeraldDialogTypes.Success:
      return (
        <img
          src={Checkmark}
          alt={'Checkmark'}
          className={`${classes.iconAlternate} ${classes.success}`}
        />
      );
    default:
      return <CancelIcon classes={{ root: `${classes.icon} ${classes.error}` }} />;
  }
};

export const DialogBar: React.FC<EmeraldDialogFrameProps> = (
  props: EmeraldDialogFrameProps,
): JSX.Element => {
  const classes = styles();
  const isVerticallyCentered = props.verticalAlignment === VerticalAlignment.Center;
  switch (props.type) {
    case EmeraldDialogTypes.Status:
    case EmeraldDialogTypes.Info:
      return (
        <div
          className={clsx(
            classes.barNotification,
            classes.bar,
            isVerticallyCentered && classes.centeredBar,
          )}
        />
      );
    case EmeraldDialogTypes.InfoDecision:
      return (
        <div
          className={clsx(
            classes.barInfoDecision,
            classes.bar,
            isVerticallyCentered && classes.centeredBar,
          )}
        />
      );
    case EmeraldDialogTypes.Warning:
      return (
        <div
          className={clsx(
            classes.barWarning,
            classes.bar,
            isVerticallyCentered && classes.centeredBar,
          )}
        />
      );
    case EmeraldDialogTypes.Success:
      return (
        <div
          className={clsx(
            classes.barSuccess,
            classes.bar,
            isVerticallyCentered && classes.centeredBar,
          )}
        />
      );
    case EmeraldDialogTypes.None: {
      return <></>;
    }
    default:
      return (
        <div
          className={clsx(
            classes.barError,
            classes.bar,
            isVerticallyCentered && classes.centeredBar,
          )}
        />
      );
  }
};

const DialogHeader: React.FC<EmeraldDialogFrameProps> = (
  props: EmeraldDialogFrameProps,
): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();

  if (props.header) {
    return <span className={classes.headerSpacing}>{t(props.header)}</span>;
  }

  switch (props.type) {
    case EmeraldDialogTypes.Warning:
      return <span className={classes.spacing} />;
    case EmeraldDialogTypes.Status:
      return <span className={classes.spacing}>{t('Package_Status', '')}</span>;
    case EmeraldDialogTypes.Prohibit:
      return <span className={classes.spacing} />;
    case EmeraldDialogTypes.Error:
    case EmeraldDialogTypes.InfoDecision:
    case EmeraldDialogTypes.Info:
    default:
      return <span className={classes.spacing} />;
  }
};

const GenericDialog: React.FC<EmeraldDialogProps> = ({
  message,
  subMessage,
  subMessage2,
  subMessageArgs,
  verticalAlignment,
  dataTestIdPrefix,
  type,
  dialogContentComponent,
  dialogContentAboveSubMessage,
}: EmeraldDialogProps): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();

  if (typeof subMessage === 'string') {
    subMessage = t(subMessage as string, subMessageArgs); // the typeof check isn't enough to prevent type error here
  }

  return (
    <Grid
      container
      item
      justifyContent='center'
      direction='column'
      className={clsx(
        classes.grow1Shrink1,
        verticalAlignment === VerticalAlignment.Center && classes.grow1Shrink1Centered,
      )}
    >
      <Typography variant='h2' data-testid={`${dataTestIdPrefix || 'p'}-dialog-message`}>
        {t(message ?? '')}
      </Typography>
      <DialogBar type={type} verticalAlignment={verticalAlignment} />

      {dialogContentAboveSubMessage && <div>{dialogContentComponent}</div>}

      <div className={classes.message} data-testid={`${dataTestIdPrefix || 'p'}-dialog-submessage`}>
        {subMessage}
      </div>
      {subMessage2 && (
        <div
          className={`${classes.message} ${classes.subMessage2}`}
          data-testid={`${dataTestIdPrefix || 'p'}-dialog-submessage2`}
        >
          {t(subMessage2)}
        </div>
      )}
    </Grid>
  );
};

// TODO textContent is not taking in its parent container height and overflows. Not Dynamic.
export const InteractiveDialogContent: React.FC<EmeraldDialogProps> = (
  props: EmeraldDialogProps,
): JSX.Element => {
  const classes = styles();
  const verticalAlignment = props.verticalAlignment;
  return (
    <Grid
      item
      xs={12}
      className={clsx(verticalAlignment === VerticalAlignment.Center && classes.centeredMessage)}
    >
      <Grid
        className={clsx(
          classes.header,
          verticalAlignment === VerticalAlignment.Center && classes.centeredHeader,
        )}
        container
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        data-testid={`${props.dataTestIdPrefix || 'p'}-dialog`}
      >
        <Grid item data-testid={`${props.dataTestIdPrefix || 'p'}-dialog-header`}>
          <DialogHeader
            type={props.type}
            header={props.header}
            verticalAlignment={props.verticalAlignment}
          />
        </Grid>

        <Grid item data-testid={`${props.dataTestIdPrefix || 'p'}-dialog-icon-${props.type}`}>
          <DialogIcon type={props.type} />
        </Grid>
      </Grid>
      {props.textContent ? props.textContent : <GenericDialog {...props} />}
      {props.dialogContentAboveSubMessage ? '' : props.dialogContentComponent}
    </Grid>
  );
};
export interface EmeraldDialogProps {
  type: EmeraldDialogTypes;
  header?: string;
  message?: string;
  messageArgs?: InterpolationArguments;
  subMessage?: string | JSX.Element[] | JSX.Element;
  subMessage2?: string;
  subMessageArgs?: InterpolationArguments;
  dialogContentComponent?: JSX.Element;
  dialogContentAboveSubMessage?: boolean;
  dataTestIdPrefix?: string;
  verticalAlignment?: VerticalAlignment;
  textContent?: JSX.Element;
  showDialogBar?: boolean;
  dialogStyle?: CSSProperties;
}

interface EmeraldDialogFrameProps {
  type: EmeraldDialogTypes;
  header?: string;
  verticalAlignment?: VerticalAlignment;
}

export const MessageModal: React.FC<IMessageModal> = (props: IMessageModal): JSX.Element => {
  const defaultAcceptLabel = props.type === EmeraldDialogTypes.Info ? 'Ok' : 'Close';
  const size = props.size || MessageModalSizeEnum.Medium;
  const { t } = useTranslation();

  let width: number | undefined;
  let height: number | undefined;
  // Localize text props
  const title = t(props.title || '');
  const message = t(props.messageOverride ?? props.message ?? '', props.messageArgs);

  let subMessage = props.subMessage;
  if (typeof subMessage === 'string') {
    subMessage = props.subMessageOverride
      ? t(props.subMessageOverride ?? props.subMessage ?? '', props.subMessage)
      : t(subMessage as string, props.subMessageArgs); // the typeof check isn't enough to prevent type error here
  }
  const fullScreenStyle = {
    width: 100,
    height: 100,
  };
  const extraLargeStyle = {
    width: 62.3,
    height: 78.65,
  };
  const largeStyle = {
    width: 64.7,
  };
  const mediumStyle = {
    width: 46.85,
  };

  switch (size) {
    case MessageModalSizeEnum.FullScreen:
      width = fullScreenStyle.width;
      height = fullScreenStyle.height;
      break;
    case MessageModalSizeEnum.ExtraLarge:
      width = extraLargeStyle.width;
      height = extraLargeStyle.height;
      break;
    case MessageModalSizeEnum.Large:
      width = largeStyle.width;
      break;
    case MessageModalSizeEnum.Medium:
      width = mediumStyle.width;
      break;
    default:
      width = mediumStyle.width;
  }
  width = props.dialogWidth ?? width;
  height = props.dialogHeight ?? height;
  return (
    <InteractiveDialogueContainer
      dialogTestIdPrefix={props.dialogTestIdPrefix || 'dialog-messages'}
      open={true}
      disableBackdropClick={props.disableBackdropClick}
      hideButtons={props.hideButtons}
      hideCancelButton={props.hideCancelButton}
      hideAcceptButton={props.hideAcceptButton}
      acceptButtonLabel={(props.acceptButtonLabel ?? '') || defaultAcceptLabel}
      closeButtonLabel={(props.closeButtonLabel ?? '') || undefined}
      handleClose={props.onCancel || props.onAccept}
      handleAccept={props.onAccept}
      component={
        props.component || (
          <InteractiveDialogContent
            type={props.type || EmeraldDialogTypes.Info}
            header={title || ''}
            message={message}
            subMessage={subMessage || ''}
            subMessage2={props.subMessage2}
            dialogContentComponent={props.dialogContentComponent}
            dialogContentAboveSubMessage={props.dialogContentAboveSubMessage}
            dataTestIdPrefix='messages'
            verticalAlignment={props.verticalAlignment}
            textContent={props.textContent}
          ></InteractiveDialogContent>
        )
      }
      buttonComponent={props.buttonComponent}
      dialogWidth={width}
      dialogHeight={height}
      separateButton={props.separateButton}
      leftButtons={props.leftButtons}
      rightButtons={props.rightButtons}
      centerButtons={props.centerButtons}
      fullScreen={props.fullScreen}
    />
  );
};

export const buildEmeraldModalProps = (
  dialogProps: EmeraldDialogProps,
  modalProps: IModal,
): IMessageModal => {
  const component = <InteractiveDialogContent {...dialogProps} />;
  return {
    ...modalProps,
    component,
  };
};

export const showEmeraldDialog = (props: IMessageModal): void => {
  pushEmeraldModal(<MessageModal {...props} />, undefined, props.modalId);
};

export default InteractiveDialogContent;
